/**
 * Hook for managing drag-and-drop reordering of table rows.
 * Handles drag state, drop indicators, and computes fractional index bounds.
 */

import { useState, DragEvent, useEffect } from "react";

type DragIndicatorPosition = {
  rowId: string;
  position: "before" | "after";
};

type RowWithId = {
  id: string;
};

type UseDragAndDropProps<T extends RowWithId> = {
  rows: T[];
  canEdit: boolean;
  isReordering: boolean;
  onReorder?: (
    entryId: string,
    bounds: { beforeId?: string; afterId?: string }
  ) => void;
};

export function useDragAndDrop<T extends RowWithId>({
  rows,
  canEdit,
  isReordering,
  onReorder,
}: UseDragAndDropProps<T>) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropIndicatorPosition, setDropIndicatorPosition] =
    useState<DragIndicatorPosition | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      document.body.classList.remove("is-dragging");
      document.body.style.cursor = "";
      const styleEl = document.getElementById("drag-pointer-events-override");
      styleEl?.remove();
    };
  }, []);

  const handleDragStart =
    (row: T) => (event: DragEvent<HTMLTableRowElement>) => {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", row.id);
      setDraggingId(row.id);

      document.body.style.cursor = "grabbing";
      document.body.classList.add("is-dragging");

      const styleId = "drag-pointer-events-override";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        // Make cell content transparent to hit-testing so dragover events
        // target the cells themselves. Only cell *descendants* may be
        // targeted: setting pointer-events on the dragged row's cells makes
        // Chromium cancel the drag right after it starts.
        style.textContent = `
          .is-dragging td *,
          .is-dragging th * {
            pointer-events: none !important;
          }
        `;
        document.head.append(style);
      }
    };

  const handleDragEnd = () => {
    cleanupDrag();
  };

  const handleContainerDragOver = (event: DragEvent<HTMLElement>) => {
    if (!canEdit || isReordering || !draggingId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    // We know it's a valid HTMLElement because it's a table row, so cast OK.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const target = event.target as HTMLElement;
    const row = target.closest("[data-row-id]");

    if (!row) {
      setDropIndicatorPosition(null);
      return;
    }

    const rowId = row.getAttribute("data-row-id");
    if (!rowId || rowId === draggingId) return;

    const sourceIndex = rows.findIndex((r) => r.id === draggingId);
    const targetIndex = rows.findIndex((r) => r.id === rowId);

    if (sourceIndex === -1 || targetIndex === -1) return;

    const draggingDown = sourceIndex < targetIndex;

    setDropIndicatorPosition({
      rowId,
      position: draggingDown ? "after" : "before",
    });
  };

  const handleContainerDrop = (event: DragEvent<HTMLElement>) => {
    if (!canEdit || isReordering || !draggingId) {
      cleanupDrag();
      return;
    }

    event.preventDefault();

    // We know it's a valid HTMLElement because it's a table row, so cast OK.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const target = event.target as HTMLElement;
    const row = target.closest("[data-row-id]");

    if (!row) {
      cleanupDrag();
      return;
    }

    const rowId = row.getAttribute("data-row-id");
    const sourceId = draggingId;

    if (!sourceId || !rowId || sourceId === rowId) {
      cleanupDrag();
      return;
    }

    const indicator = dropIndicatorPosition ?? {
      rowId,
      position: "after",
    };

    // Remove the dragged item from the array to find the new position
    const rowsWithoutSource = rows.filter((r) => r.id !== sourceId);
    const targetIndex = rowsWithoutSource.findIndex(
      (r) => r.id === indicator.rowId
    );

    if (targetIndex === -1) {
      cleanupDrag();
      return;
    }

    // Calculate insertion point based on indicator position
    const insertIndex =
      indicator.position === "after" ? targetIndex + 1 : targetIndex;

    // Get the items before and after the insertion point for fractional indexing
    const beforeEntry = rowsWithoutSource[insertIndex - 1];
    const afterEntry = rowsWithoutSource[insertIndex];

    const bounds = {
      beforeId: beforeEntry?.id,
      afterId: afterEntry?.id,
    };

    onReorder?.(sourceId, bounds);
    cleanupDrag();
  };

  const cleanupDrag = () => {
    setDraggingId(null);
    setDropIndicatorPosition(null);
    document.body.style.cursor = "";
    document.body.classList.remove("is-dragging");

    const styleEl = document.getElementById("drag-pointer-events-override");
    if (styleEl) {
      styleEl.remove();
    }
  };

  // Returns props to spread onto a draggable row element.
  const getItemProps = (row: T) => {
    const showBefore =
      dropIndicatorPosition?.rowId === row.id &&
      dropIndicatorPosition?.position === "before";
    const showAfter =
      dropIndicatorPosition?.rowId === row.id &&
      dropIndicatorPosition?.position === "after";
    const isDragging = draggingId === row.id;

    // dragover and drop are handled only by the container (via bubbling);
    // registering them here too would run each handler twice per event and
    // send duplicate reorder requests.
    return {
      draggable: canEdit,
      onDragStart: handleDragStart(row),
      onDragEnd: handleDragEnd,
      "data-dragging": isDragging ? "true" : undefined,
      "data-row-id": row.id,
      "data-indicator-before": showBefore ? "true" : undefined,
      "data-indicator-after": showAfter ? "true" : undefined,
    };
  };

  const containerProps = {
    onDragOver: handleContainerDragOver,
    onDrop: handleContainerDrop,
  };

  return {
    getItemProps,
    containerProps,
  };
}
